import type { StateCreator } from 'zustand'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { toast } from 'sonner'
import { apiService } from '@/service/api'
import type {
  OnboardingQuestion,
  OnboardingAnswer,
  OnboardingAllUsersAnswersResponse,
  CreateOnboardingQuestionRequest,
  UpdateOnboardingQuestionRequest,
  SubmitOnboardingAnswersRequest,
} from '@/service/api'

export interface OnboardingQuestionsState {
  // State
  onboardingQuestions: OnboardingQuestion[]
  myOnboardingAnswers: OnboardingAnswer[]
  allUsersAnswers: OnboardingAllUsersAnswersResponse | null
  isOnboardingQuestionsLoading: boolean
  hasCompletedOnboarding: boolean | null

  // Actions - Questions
  fetchOnboardingQuestions: () => Promise<void>
  fetchOnboardingQuestionsAdmin: () => Promise<void>
  createQuestion: (request: CreateOnboardingQuestionRequest) => Promise<void>
  updateQuestion: (id: string, request: UpdateOnboardingQuestionRequest) => Promise<void>
  deleteQuestion: (id: string) => Promise<void>

  // Actions - Answers
  fetchMyAnswers: () => Promise<void>
  submitAnswers: (request: SubmitOnboardingAnswersRequest) => Promise<void>
  fetchAllUsersAnswers: () => Promise<void>
  checkOnboardingStatus: () => Promise<boolean>

  // Helpers
  setOnboardingQuestionsLoading: (loading: boolean) => void
}

const messages = {
  success: {
    createQuestion: 'Question created successfully',
    updateQuestion: 'Question updated successfully',
    deleteQuestion: 'Question deleted successfully',
    submitAnswers: 'Answers submitted successfully',
  },
  error: {
    fetchQuestions: 'Error fetching onboarding questions',
    createQuestion: 'Error creating question',
    updateQuestion: 'Error updating question',
    deleteQuestion: 'Error deleting question',
    fetchAnswers: 'Error fetching answers',
    submitAnswers: 'Error submitting answers',
    checkStatus: 'Error checking onboarding status',
  },
}

export const onboardingQuestionsSlice: StateCreator<
  OnboardingQuestionsState & loadingState & authSessionState,
  [],
  [],
  OnboardingQuestionsState
> = (set, get) => ({
  onboardingQuestions: [],
  myOnboardingAnswers: [],
  allUsersAnswers: null,
  isOnboardingQuestionsLoading: false,
  hasCompletedOnboarding: null,

  setOnboardingQuestionsLoading: (loading) => {
    set({ isOnboardingQuestionsLoading: loading })
  },

  // ==================== QUESTION ACTIONS ====================

  /**
   * Fetches active onboarding questions for the tenant.
   * Used during the onboarding flow.
   */
  fetchOnboardingQuestions: async () => {
    const { setOnboardingQuestionsLoading } = get()
    try {
      setOnboardingQuestionsLoading(true)
      const questions = await apiService.getOnboardingQuestions()
      set({ onboardingQuestions: questions })
    } catch {
      toast.error(messages.error.fetchQuestions)
      set({ onboardingQuestions: [] })
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Fetches all questions for admin management (including inactive).
   */
  fetchOnboardingQuestionsAdmin: async () => {
    const { setOnboardingQuestionsLoading } = get()
    try {
      setOnboardingQuestionsLoading(true)
      const questions = await apiService.getOnboardingQuestionsAdmin()
      set({ onboardingQuestions: questions })
    } catch {
      toast.error(messages.error.fetchQuestions)
      set({ onboardingQuestions: [] })
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Creates a new onboarding question (Admin only).
   */
  createQuestion: async (request: CreateOnboardingQuestionRequest) => {
    const { setOnboardingQuestionsLoading, fetchOnboardingQuestionsAdmin } = get()
    try {
      setOnboardingQuestionsLoading(true)
      await apiService.createOnboardingQuestion(request)
      await fetchOnboardingQuestionsAdmin()
      toast.success(messages.success.createQuestion)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : messages.error.createQuestion
      toast.error(errorMessage)
      throw error
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Updates an existing question (Admin only).
   */
  updateQuestion: async (id: string, request: UpdateOnboardingQuestionRequest) => {
    const { setOnboardingQuestionsLoading, fetchOnboardingQuestionsAdmin } = get()
    try {
      setOnboardingQuestionsLoading(true)
      await apiService.updateOnboardingQuestion(id, request)
      await fetchOnboardingQuestionsAdmin()
      toast.success(messages.success.updateQuestion)
    } catch {
      toast.error(messages.error.updateQuestion)
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Deletes a question (Admin only, soft delete).
   */
  deleteQuestion: async (id: string) => {
    const { setOnboardingQuestionsLoading, fetchOnboardingQuestionsAdmin } = get()
    try {
      setOnboardingQuestionsLoading(true)
      await apiService.deleteOnboardingQuestion(id)
      await fetchOnboardingQuestionsAdmin()
      toast.success(messages.success.deleteQuestion)
    } catch {
      toast.error(messages.error.deleteQuestion)
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  // ==================== ANSWER ACTIONS ====================

  /**
   * Fetches the current user's onboarding answers.
   */
  fetchMyAnswers: async () => {
    const { setOnboardingQuestionsLoading } = get()
    try {
      setOnboardingQuestionsLoading(true)
      const response = await apiService.getMyOnboardingAnswers()
      set({ myOnboardingAnswers: response.answers })
    } catch {
      toast.error(messages.error.fetchAnswers)
      set({ myOnboardingAnswers: [] })
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Submits or updates the user's onboarding answers.
   */
  submitAnswers: async (request: SubmitOnboardingAnswersRequest) => {
    const { setOnboardingQuestionsLoading, fetchMyAnswers, checkOnboardingStatus } = get()
    try {
      setOnboardingQuestionsLoading(true)
      await apiService.submitOnboardingAnswers(request)
      await fetchMyAnswers()
      await checkOnboardingStatus()
      toast.success(messages.success.submitAnswers)
    } catch {
      toast.error(messages.error.submitAnswers)
      throw new Error(messages.error.submitAnswers)
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Fetches all users' answers (Admin only).
   */
  fetchAllUsersAnswers: async () => {
    const { setOnboardingQuestionsLoading } = get()
    try {
      setOnboardingQuestionsLoading(true)
      const response = await apiService.getAllUsersOnboardingAnswers()
      set({ allUsersAnswers: response })
    } catch {
      toast.error(messages.error.fetchAnswers)
      set({ allUsersAnswers: null })
    } finally {
      setOnboardingQuestionsLoading(false)
    }
  },

  /**
   * Checks if the current user has completed onboarding.
   */
  checkOnboardingStatus: async () => {
    try {
      const response = await apiService.getOnboardingStatus()
      set({ hasCompletedOnboarding: response.completed })
      return response.completed
    } catch {
      // Silent fail - don't show toast for status check
      set({ hasCompletedOnboarding: null })
      return false
    }
  },
})
